// AdminInvoicesPage.js — functional AR console for HOA admins
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { auth } from "./firebase.js";
import { toast } from "react-toastify";

function formatMoney(cents, currency = "usd") {
  const amount = (Number(cents) || 0) / 100;
  try {
    return amount.toLocaleString("en-US", {
      style: "currency",
      currency: (currency || "usd").toUpperCase(),
    });
  } catch {
    return `$${amount.toFixed(2)}`;
  }
}

function formatDate(unixSeconds) {
  if (!unixSeconds) return "—";
  const ms = typeof unixSeconds === "number" && unixSeconds < 1e12 ? unixSeconds * 1000 : unixSeconds;
  return new Date(ms).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function daysUntilDue(dueUnix) {
  if (!dueUnix) return null;
  const due = new Date(dueUnix * 1000);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  return Math.round((due - today) / 86400000);
}

function displayStatus(invoice) {
  return invoice.displayStatus || invoice.status || "unknown";
}

function statusLabel(invoice) {
  const state = displayStatus(invoice);
  if (state === "processing") return "Processing";
  if (state === "authentication_required") return "Auth required";
  if (state === "paid") return "Paid";
  if (state === "open") return "Open";
  if (state === "draft") return "Draft";
  if (state === "void") return "Void";
  if (state === "uncollectible") return "Uncollectible";
  return state;
}

function isOpenLike(invoice) {
  const s = displayStatus(invoice);
  return s === "open" || s === "authentication_required";
}

function isOverdue(invoice) {
  if (!isOpenLike(invoice)) return false;
  const days = daysUntilDue(invoice.due_date);
  return days !== null && days < 0;
}

function isDueSoon(invoice) {
  if (!isOpenLike(invoice)) return false;
  const days = daysUntilDue(invoice.due_date);
  return days !== null && days >= 0 && days <= 7;
}

function csvEscape(value) {
  const s = String(value ?? "");
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadCsv(filename, rows) {
  const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const STATUS_TABS = [
  { id: "action", label: "Needs attention" },
  { id: "open", label: "Open" },
  { id: "overdue", label: "Overdue" },
  { id: "processing", label: "Processing" },
  { id: "paid", label: "Paid" },
  { id: "draft", label: "Draft" },
  { id: "void", label: "Void" },
  { id: "all", label: "All" },
];

const SORT_OPTIONS = [
  { id: "due_asc", label: "Due date (soonest)" },
  { id: "due_desc", label: "Due date (latest)" },
  { id: "amount_desc", label: "Amount (high → low)" },
  { id: "amount_asc", label: "Amount (low → high)" },
  { id: "created_desc", label: "Created (newest)" },
  { id: "created_asc", label: "Created (oldest)" },
  { id: "lot_asc", label: "Lot" },
  { id: "name_asc", label: "Owner name" },
];

const AdminInvoicesPage = ({ isMobile }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const [statusTab, setStatusTab] = useState("action");
  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState("due_asc");
  const [unlinkedOnly, setUnlinkedOnly] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());

  const fetchAllInvoices = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) {
      toast.error("Please log in to view invoices");
      setLoading(false);
      return;
    }
    setError(null);
    try {
      const idToken = await user.getIdToken();
      const response = await fetch(
        "https://us-central1-nantahala-mountain-resor-596d9.cloudfunctions.net/api/getAllInvoices",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({}),
        },
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to fetch all invoices");
      }
      const data = await response.json();
      setInvoices(data.invoices || []);
      setSelected(new Set());
    } catch (err) {
      console.error("Error fetching all invoices:", err.message);
      setError(err.message);
      toast.error(`Error fetching invoices: ${err.message}`);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchAllInvoices();
  }, [fetchAllInvoices]);

  const refresh = async () => {
    setRefreshing(true);
    await fetchAllInvoices();
  };

  const counts = useMemo(() => {
    const c = {
      all: invoices.length,
      open: 0,
      overdue: 0,
      processing: 0,
      paid: 0,
      draft: 0,
      void: 0,
      action: 0,
      unlinked: 0,
    };
    for (const inv of invoices) {
      const s = displayStatus(inv);
      if (s === "open" || s === "authentication_required") c.open += 1;
      if (isOverdue(inv)) c.overdue += 1;
      if (s === "processing") c.processing += 1;
      if (s === "paid") c.paid += 1;
      if (s === "draft" || inv.stripeStatus === "draft") c.draft += 1;
      if (s === "void" || inv.stripeStatus === "void") c.void += 1;
      if (isOverdue(inv) || s === "authentication_required" || s === "processing" || inv.linkedToOwner === false) {
        c.action += 1;
      }
      if (inv.linkedToOwner === false) c.unlinked += 1;
    }
    return c;
  }, [invoices]);

  const summary = useMemo(() => {
    let outstanding = 0;
    let overdue = 0;
    let paid = 0;
    let processing = 0;
    for (const inv of invoices) {
      const s = displayStatus(inv);
      if (isOpenLike(inv)) {
        const rem = inv.amount_remaining ?? inv.amount_due ?? 0;
        outstanding += rem;
        if (isOverdue(inv)) overdue += rem;
      }
      if (s === "paid") paid += inv.amount_paid ?? inv.amount_due ?? 0;
      if (s === "processing") processing += inv.amount_due ?? 0;
    }
    return { outstanding, overdue, paid, processing };
  }, [invoices]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = invoices.filter((inv) => {
      const s = displayStatus(inv);
      const stripe = inv.stripeStatus || inv.status;
      if (statusTab === "open") return isOpenLike(inv);
      if (statusTab === "overdue") return isOverdue(inv);
      if (statusTab === "processing") return s === "processing";
      if (statusTab === "paid") return s === "paid";
      if (statusTab === "draft") return s === "draft" || stripe === "draft";
      if (statusTab === "void") return s === "void" || stripe === "void";
      if (statusTab === "action") {
        return (
          isOverdue(inv) ||
          s === "authentication_required" ||
          s === "processing" ||
          inv.linkedToOwner === false
        );
      }
      return true;
    });

    if (unlinkedOnly) {
      list = list.filter((inv) => inv.linkedToOwner === false);
    }

    if (q) {
      list = list.filter((inv) => {
        const hay = [
          inv.number,
          inv.id,
          inv.name1,
          inv.customer_name,
          inv.customer_email,
          inv.customer_phone,
          inv.lotNumber,
          inv.lotAddress,
          inv.description,
          inv.stripeCustomerId,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      });
    }

    const remaining = (inv) => inv.amount_remaining ?? inv.amount_due ?? 0;
    list = [...list].sort((a, b) => {
      switch (sortBy) {
        case "due_asc":
          return (a.due_date || 9e12) - (b.due_date || 9e12);
        case "due_desc":
          return (b.due_date || 0) - (a.due_date || 0);
        case "amount_desc":
          return remaining(b) - remaining(a);
        case "amount_asc":
          return remaining(a) - remaining(b);
        case "created_asc":
          return (a.created || 0) - (b.created || 0);
        case "lot_asc":
          return String(a.lotNumber || "").localeCompare(String(b.lotNumber || ""), undefined, {
            numeric: true,
          });
        case "name_asc":
          return String(a.name1 || a.customer_name || "").localeCompare(
            String(b.name1 || b.customer_name || ""),
          );
        case "created_desc":
        default:
          return (b.created || 0) - (a.created || 0);
      }
    });

    return list;
  }, [invoices, statusTab, query, sortBy, unlinkedOnly]);

  const filteredOutstanding = useMemo(
    () =>
      filtered.reduce((sum, inv) => {
        if (!isOpenLike(inv)) return sum;
        return sum + (inv.amount_remaining ?? inv.amount_due ?? 0);
      }, 0),
    [filtered],
  );

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    if (filtered.every((inv) => selected.has(inv.id))) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((inv) => inv.id)));
    }
  };

  const copyText = async (text, label = "Copied") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label);
    } catch {
      toast.error("Could not copy");
    }
  };

  const exportCsv = (rows) => {
    const header = [
      "invoice_number",
      "stripe_id",
      "status",
      "stripe_status",
      "owner_name",
      "customer_name",
      "customer_email",
      "lot",
      "amount_due",
      "amount_paid",
      "amount_remaining",
      "currency",
      "created",
      "due_date",
      "paid_at",
      "description",
      "linked_to_owner",
      "hosted_url",
    ];
    const lines = [header.join(",")];
    for (const inv of rows) {
      lines.push(
        [
          inv.number,
          inv.id,
          displayStatus(inv),
          inv.stripeStatus || inv.status,
          inv.name1,
          inv.customer_name,
          inv.customer_email,
          inv.lotNumber,
          ((inv.amount_due || 0) / 100).toFixed(2),
          ((inv.amount_paid || 0) / 100).toFixed(2),
          ((inv.amount_remaining || 0) / 100).toFixed(2),
          inv.currency || "usd",
          inv.created ? new Date(inv.created * 1000).toISOString().slice(0, 10) : "",
          inv.due_date ? new Date(inv.due_date * 1000).toISOString().slice(0, 10) : "",
          inv.paid_at ? new Date(inv.paid_at * 1000).toISOString().slice(0, 10) : "",
          inv.description,
          inv.linkedToOwner === false ? "no" : "yes",
          inv.hosted_invoice_url,
        ]
          .map(csvEscape)
          .join(","),
      );
    }
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`nmr-invoices-${stamp}.csv`, lines);
    toast.success(`Exported ${rows.length} invoices`);
  };

  if (loading) {
    return <div className="ai-loading">Loading all invoices from Stripe…</div>;
  }

  return (
    <div className={`ai-page ${isMobile ? "ai-page--mobile" : ""}`}>
      <header className="ai-hero">
        <div>
          <p className="ai-eyebrow">Admin · Accounts receivable</p>
          <h1 className="ai-title">All invoices</h1>
          <p className="ai-subtitle">
            Search, filter, and act on HOA invoices. Data is live from Stripe with owner/lot links from
            Firestore.
          </p>
        </div>
        <div className="ai-hero-actions">
          <button type="button" className="ai-btn ai-btn--secondary" onClick={refresh} disabled={refreshing}>
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
          <button
            type="button"
            className="ai-btn ai-btn--primary"
            onClick={() => exportCsv(filtered)}
            disabled={filtered.length === 0}
          >
            Export filtered CSV
          </button>
        </div>
      </header>

      {error && (
        <div className="ai-alert" role="alert">
          <strong>Could not load invoices.</strong> {error}{" "}
          <button type="button" className="ai-link" onClick={refresh}>
            Retry
          </button>
        </div>
      )}

      <section className="ai-kpis" aria-label="AR summary">
        <div className="ai-kpi ai-kpi--warn">
          <div className="ai-kpi-label">Open AR</div>
          <div className="ai-kpi-value">{formatMoney(summary.outstanding)}</div>
          <div className="ai-kpi-hint">{counts.open} open invoices</div>
        </div>
        <div className="ai-kpi ai-kpi--danger">
          <div className="ai-kpi-label">Overdue AR</div>
          <div className="ai-kpi-value">{formatMoney(summary.overdue)}</div>
          <div className="ai-kpi-hint">{counts.overdue} past due</div>
        </div>
        <div className="ai-kpi">
          <div className="ai-kpi-label">Processing</div>
          <div className="ai-kpi-value">{formatMoney(summary.processing)}</div>
          <div className="ai-kpi-hint">{counts.processing} clearing</div>
        </div>
        <div className="ai-kpi ai-kpi--ok">
          <div className="ai-kpi-label">Paid (all loaded)</div>
          <div className="ai-kpi-value">{formatMoney(summary.paid)}</div>
          <div className="ai-kpi-hint">{counts.paid} paid invoices</div>
        </div>
      </section>

      <section className="ai-toolbar">
        <div className="ai-tabs" role="tablist" aria-label="Status filter">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={statusTab === tab.id}
              className={`ai-tab ${statusTab === tab.id ? "is-active" : ""}`}
              onClick={() => setStatusTab(tab.id)}
            >
              {tab.label}
              <span className="ai-tab-count">{counts[tab.id] ?? counts.all}</span>
            </button>
          ))}
        </div>

        <div className="ai-filters">
          <input
            type="search"
            className="ai-search"
            placeholder="Search owner, email, lot, invoice #…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search invoices"
          />
          <label className="ai-select-label">
            Sort
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="ai-select">
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          <label className="ai-check">
            <input
              type="checkbox"
              checked={unlinkedOnly}
              onChange={(e) => setUnlinkedOnly(e.target.checked)}
            />
            Unlinked to owner only ({counts.unlinked})
          </label>
        </div>

        <div className="ai-result-bar">
          <span>
            Showing <strong>{filtered.length}</strong> of {invoices.length}
            {statusTab !== "all" || query || unlinkedOnly
              ? ` · Filtered open balance ${formatMoney(filteredOutstanding)}`
              : ""}
          </span>
          {selected.size > 0 && (
            <span className="ai-selected-actions">
              {selected.size} selected ·{" "}
              <button
                type="button"
                className="ai-link"
                onClick={() => {
                  const rows = invoices.filter((inv) => selected.has(inv.id));
                  exportCsv(rows);
                }}
              >
                Export selected
              </button>
              {" · "}
              <button type="button" className="ai-link" onClick={() => setSelected(new Set())}>
                Clear
              </button>
            </span>
          )}
        </div>
      </section>

      {filtered.length === 0 ? (
        <div className="ai-empty">No invoices match this view. Try another status tab or clear search.</div>
      ) : (
        <div className="ai-table-wrap">
          <table className="ai-table">
            <thead>
              <tr>
                <th className="ai-col-check">
                  <input
                    type="checkbox"
                    checked={filtered.length > 0 && filtered.every((inv) => selected.has(inv.id))}
                    onChange={toggleSelectAllVisible}
                    aria-label="Select all visible"
                  />
                </th>
                <th>Invoice</th>
                <th>Owner / customer</th>
                <th>Lot</th>
                <th>Status</th>
                <th className="ai-num">Remaining</th>
                <th>Due</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((inv) => {
                const expanded = expandedId === inv.id;
                const rem = inv.amount_remaining ?? inv.amount_due ?? 0;
                const days = daysUntilDue(inv.due_date);
                const s = displayStatus(inv);
                return (
                  <React.Fragment key={inv.id}>
                    <tr
                      className={[
                        "ai-row",
                        isOverdue(inv) ? "ai-row--overdue" : "",
                        isDueSoon(inv) ? "ai-row--soon" : "",
                        inv.linkedToOwner === false ? "ai-row--unlinked" : "",
                        expanded ? "ai-row--open" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      <td className="ai-col-check">
                        <input
                          type="checkbox"
                          checked={selected.has(inv.id)}
                          onChange={() => toggleSelect(inv.id)}
                          aria-label={`Select ${inv.number || inv.id}`}
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="ai-invoice-id"
                          onClick={() => setExpandedId(expanded ? null : inv.id)}
                        >
                          <strong>{inv.number || inv.id}</strong>
                          <span className="ai-muted">{formatDate(inv.created)}</span>
                        </button>
                      </td>
                      <td>
                        <div className="ai-owner">
                          <strong>{inv.name1 && inv.name1 !== "N/A" ? inv.name1 : inv.customer_name}</strong>
                          <span className="ai-muted">{inv.customer_email}</span>
                          {inv.linkedToOwner === false && (
                            <span className="ai-chip ai-chip--warn">No owner link</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="ai-lot">
                          {inv.lotNumber && inv.lotNumber !== "N/A" ? inv.lotNumber : "—"}
                        </span>
                      </td>
                      <td>
                        <span className={`ai-status ai-status--${String(s).replace(/_/g, "-")}`}>
                          {statusLabel(inv)}
                        </span>
                        {isOverdue(inv) && (
                          <span className="ai-chip ai-chip--danger">
                            {Math.abs(days)}d overdue
                          </span>
                        )}
                        {!isOverdue(inv) && isDueSoon(inv) && (
                          <span className="ai-chip ai-chip--warn">
                            {days === 0 ? "Due today" : `${days}d left`}
                          </span>
                        )}
                      </td>
                      <td className="ai-num">
                        <strong>{formatMoney(rem, inv.currency)}</strong>
                        {inv.amount_paid > 0 && (
                          <span className="ai-muted">paid {formatMoney(inv.amount_paid, inv.currency)}</span>
                        )}
                      </td>
                      <td>
                        <div>{formatDate(inv.due_date)}</div>
                      </td>
                      <td className="ai-actions">
                        <button
                          type="button"
                          className="ai-link"
                          onClick={() => setExpandedId(expanded ? null : inv.id)}
                        >
                          {expanded ? "Hide" : "Details"}
                        </button>
                        {inv.hosted_invoice_url && (
                          <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer">
                            Stripe
                          </a>
                        )}
                        {inv.invoice_pdf && (
                          <a href={inv.invoice_pdf} target="_blank" rel="noopener noreferrer">
                            PDF
                          </a>
                        )}
                        {inv.customer_email && inv.customer_email !== "N/A" && (
                          <button
                            type="button"
                            className="ai-link"
                            onClick={() => copyText(inv.customer_email, "Email copied")}
                          >
                            Copy email
                          </button>
                        )}
                      </td>
                    </tr>
                    {expanded && (
                      <tr className="ai-detail-row">
                        <td colSpan={8}>
                          <div className="ai-detail">
                            <dl className="ai-detail-grid">
                              <div>
                                <dt>Stripe invoice ID</dt>
                                <dd className="ai-mono">
                                  {inv.id}{" "}
                                  <button
                                    type="button"
                                    className="ai-link"
                                    onClick={() => copyText(inv.id, "Invoice ID copied")}
                                  >
                                    Copy
                                  </button>
                                </dd>
                              </div>
                              <div>
                                <dt>Customer (Stripe)</dt>
                                <dd>
                                  {inv.customer_name}
                                  <div className="ai-muted">{inv.customer_phone}</div>
                                </dd>
                              </div>
                              <div>
                                <dt>Address</dt>
                                <dd>{inv.customer_address || inv.lotAddress || "—"}</dd>
                              </div>
                              <div>
                                <dt>Stripe customer</dt>
                                <dd className="ai-mono">{inv.stripeCustomerId || "—"}</dd>
                              </div>
                              <div>
                                <dt>Amount due / paid / remaining</dt>
                                <dd>
                                  {formatMoney(inv.amount_due, inv.currency)} /{" "}
                                  {formatMoney(inv.amount_paid, inv.currency)} /{" "}
                                  {formatMoney(inv.amount_remaining, inv.currency)}
                                </dd>
                              </div>
                              <div>
                                <dt>Subtotal / tax / total</dt>
                                <dd>
                                  {inv.subtotal != null ? formatMoney(inv.subtotal, inv.currency) : "—"} /{" "}
                                  {inv.tax != null ? formatMoney(inv.tax, inv.currency) : "—"} /{" "}
                                  {inv.total != null ? formatMoney(inv.total, inv.currency) : "—"}
                                </dd>
                              </div>
                              <div>
                                <dt>Status (portal / Stripe)</dt>
                                <dd>
                                  {statusLabel(inv)} / {inv.stripeStatus || inv.status || "—"}
                                </dd>
                              </div>
                              <div>
                                <dt>Payment status</dt>
                                <dd>{inv.paymentStatus || "—"}</dd>
                              </div>
                              <div>
                                <dt>Collection method</dt>
                                <dd>
                                  {inv.collectionMethod === "charge_automatically"
                                    ? "Automatic charge"
                                    : inv.collectionMethod === "send_invoice"
                                      ? "Send invoice"
                                      : inv.collectionMethod || "—"}
                                </dd>
                              </div>
                              <div>
                                <dt>Attempts / next attempt</dt>
                                <dd>
                                  {inv.attemptCount ?? "—"} / {formatDate(inv.nextPaymentAttempt)}
                                </dd>
                              </div>
                              <div>
                                <dt>Paid at</dt>
                                <dd>{formatDate(inv.paid_at)}</dd>
                              </div>
                              <div>
                                <dt>Service period</dt>
                                <dd>
                                  {inv.period_start || inv.period_end
                                    ? `${formatDate(inv.period_start)} - ${formatDate(inv.period_end)}`
                                    : "—"}
                                </dd>
                              </div>
                              <div className="ai-detail-full">
                                <dt>Description</dt>
                                <dd>{inv.description || "—"}</dd>
                              </div>
                              {inv.memo ? (
                                <div className="ai-detail-full">
                                  <dt>Memo</dt>
                                  <dd>{inv.memo}</dd>
                                </div>
                              ) : null}
                              {inv.footer ? (
                                <div className="ai-detail-full">
                                  <dt>Footer</dt>
                                  <dd>{inv.footer}</dd>
                                </div>
                              ) : null}
                            </dl>

                            {Array.isArray(inv.lines) && inv.lines.length > 0 && (
                              <div className="ai-lines">
                                <h4>Line items</h4>
                                <table className="ai-lines-table">
                                  <thead>
                                    <tr>
                                      <th>Description</th>
                                      <th>Qty</th>
                                      <th className="ai-num">Amount</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {inv.lines.map((line) => (
                                      <tr key={line.id || `${line.description}-${line.amount}`}>
                                        <td>
                                          {line.description}
                                          {(line.periodStart || line.periodEnd) && (
                                            <div className="ai-muted">
                                              {formatDate(line.periodStart)} – {formatDate(line.periodEnd)}
                                            </div>
                                          )}
                                        </td>
                                        <td>{line.quantity ?? 1}</td>
                                        <td className="ai-num">
                                          {formatMoney(line.amount, inv.currency)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}

                            <div className="ai-detail-actions">
                              {inv.hosted_invoice_url && (
                                <a
                                  className="ai-btn ai-btn--primary"
                                  href={inv.hosted_invoice_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Open Stripe invoice
                                </a>
                              )}
                              {inv.invoice_pdf && (
                                <a
                                  className="ai-btn ai-btn--secondary"
                                  href={inv.invoice_pdf}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Download PDF
                                </a>
                              )}
                              {inv.customer_email && inv.customer_email !== "N/A" && (
                                <a className="ai-btn ai-btn--secondary" href={`mailto:${inv.customer_email}`}>
                                  Email owner
                                </a>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminInvoicesPage;
